// The CAPABILITY-REDIRECT seam: the router classified the message as an ACTION KIND whose capability is
// not MOUNTED on the page the user is on — e.g. a chart command from a table page, when only the dashboard
// has charts. Without this guard the kind's own proposal builder grounds against an EMPTY context and
// produces a dead-end clarification ("Which chart would you like to change?" on a page with no charts —
// no Apply, no way forward). Instead, Otto says WHY it can't do it HERE and offers to take the user where
// it CAN — as a standard two-step plan (navigate there + replay the request), so one confirmation both
// moves the user and performs the action. Same guarantees as every plan: confirm-first (the Run button is
// the one confirmation), destination grounded against the permission-scoped navTargets, and the replayed
// step re-grounds on ARRIVAL against the destination page's real options (ground-on-page — availability is
// never guessed from here).
import {
    buildPageManifest,
    pageActionLabel,
    pageReference,
    isTableViewTarget,
    isKnownPage,
    pageLabelOf,
    pageMountsCapability,
    pagesMountingCapability,
    pagesNamedIn,
    TIERS
} from "../planner/pageManifest";
import { rankPageTargets, capPageChoices } from "../planner/pageRanking";
import { describePlanStep } from "../planner/planProposal";
import { detectResidualAction, EXPORT_WORDS } from "../pageChoice/pageChoice";
import { normalizeText } from "../dashboardNavigation/navCatalog";
import { detectExportFormat } from "../tableView/tableCatalog";

// Which routed intent_kinds have a PAGE PREREQUISITE, and how to talk about it. `navigate` and
// `platform_qa` work everywhere; `multi_task` validates its own steps (planProposal); `filter_query` is
// also the classifier's error fallback, so it must never be intercepted here. `state_query` needs no entry
// either: the classifier only OFFERS that route on pages that mount `filters` (its FILTER_STATE_ADDENDUM is
// page-capability-scoped), so it can't arrive somewhere it has nothing to report.
const KIND_SPECS = {
    chart_command: {
        capability: "charts",
        missing: here => `There are no charts on ${here}`,
        home: there => `the charts live on ${there}`,
        action: "make that change"
    },
    dashboard_reset: {
        capability: "dashboard_reset",
        missing: here => `There's no dashboard to reset on ${here}`,
        home: there => `the dashboard lives on ${there}`,
        action: "reset it"
    },
    table_command: {
        capability: "table_view",
        missing: here => `There's no data table on ${here}`,
        home: there => `the tables live on ${there}`,
        action: "do that"
    }
};

// A destination mid-sentence: table views are proper names ("Buildings Table View"), everything else gets
// the natural article ("the FCA Dashboard page"). Mirrors pageActionLabel's article rule for prose.
const prose = target => (isTableViewTarget(target) ? pageReference(target) : `the ${pageReference(target)}`);

const joinProse = items => items.join(" or ");

// THE redirect itself, as plan steps: get the user to a page that CAN do this, then replay their ORIGINAL
// request there (re-grounded on arrival against that page's real options — ground-on-page). Built in one
// place because BOTH answer shapes below hand back the same two steps; the only question is which page.
const redirectSteps = (spec, target, utterance) => [
    {
        capability: "navigate",
        sub_utterance: `open the ${target.label} page`,
        target_page: target.key,
        label: pageActionLabel("Open", target)
    },
    {
        capability: spec.capability,
        sub_utterance: utterance,
        target_page: target.key,
        label: describePlanStep(
            { capability: spec.capability, sub_utterance: utterance, target_page: target.key },
            { [target.key]: target.label }
        )
    }
];

/**
 * Returns a proposal when `routedKind` can't run on `page`, or null to proceed with normal dispatch
 * (page supports it, kind has no page prerequisite, or the page is unknown to the manifest — fail open).
 *
 * Shapes returned:
 *  - one candidate page (only one mounts the capability, OR the utterance NAMED one) -> a plan proposal
 *    (navigate + replay the utterance there), rendered by PlanProposalCard and driven by the plan machinery;
 *  - several candidates -> a redirect-CHOICE proposal: one button per page, each carrying that page's
 *    complete navigate+replay plan, so the pick is the confirmation AND the original request survives it;
 *  - none reachable -> an honest message naming where the action lives (plain bubble, no dead-end card).
 */
export const buildCapabilityRedirectProposal = (routedKind, interpretResponse, ctx = {}) => {
    const spec = KIND_SPECS[routedKind];
    const { page, navTargets, utterance } = ctx;
    if (!spec || !isKnownPage(page)) return null;
    if (pageMountsCapability(page, spec.capability)) return null;

    const base = {
        capabilityRedirect: true,
        requestId: interpretResponse && interpretResponse.request_id,
        sessionId: interpretResponse && interpretResponse.session_id,
        utterance
    };
    const here = prose({ key: page, label: pageLabelOf(page) });

    // Where the user can ACTUALLY take this request: reachable pages (permission-scoped manifest) that
    // mount the capability. The current page is excluded by construction (it just failed the mount check).
    const mounts = buildPageManifest({ page, navTargets }).filter(
        p => p.page !== page && (p.capabilities || []).includes(spec.capability)
    );
    // ...narrowed by what the user ALREADY told us. "Export the recommendations" names its page, so offering
    // every page that happens to mount table_view both re-asks an answered question and produces a list that
    // grows with each table page we mount. Naming wins; nothing named falls back to the full set.
    const namedKeys = new Set(pagesNamedIn(utterance, navTargets).map(t => t.key));
    const named = mounts.filter(m => namedKeys.has(m.page));
    // ...then RANKED and CAPPED for this mount (family first, then tier), because the set of pages mounting
    // a capability grows with every page Otto is put on: without a cap, "sort that" from a chartless page
    // would eventually list every table view in the platform. Ranking makes the three that survive the
    // three most likely to be right, rather than the three that happen to come first in the catalog.
    // Manifest entries are keyed `page`; ranking reads `key` (the nav-target shape), so normalize first.
    const pool = (named.length ? named : mounts).map(m => ({ ...m, key: m.page }));
    const { shown: candidates, overflow } = capPageChoices(rankPageTargets(pool, { page, utterance }));
    const planId = base.requestId || `redirect-${routedKind}`;

    if (candidates.length === 1) {
        const target = { key: candidates[0].page, label: candidates[0].label };
        return {
            ...base,
            plan: true,
            needsClarification: false,
            planId,
            steps: redirectSteps(spec, target, utterance),
            options: [],
            message: `${spec.missing(here)} — ${spec.home(prose(target))}. Want me to take you there and ${spec.action}?`
        };
    }

    if (candidates.length > 1) {
        const targets = candidates.map(c => ({ key: c.page, label: c.label }));
        const places = joinProse(targets.map(prose));
        return {
            ...base,
            redirectChoice: true,
            needsClarification: true,
            planId,
            // Each choice IS the full redirect for that page, pre-built. Two consequences that matter: the
            // pick is the one confirmation (the pageChoice contract — no second card), and the user's ORIGINAL
            // utterance rides in step 2, so answering this question cannot lose the request that caused it.
            // The same steps are handed to the pending-clarification store, so a TYPED answer resolves here
            // too rather than being re-classified as a bare page name (which reads as "just navigate").
            choices: targets.map(target => ({
                key: target.key,
                label: pageActionLabel("Open", target),
                target,
                steps: redirectSteps(spec, target, utterance)
            })),
            // Framed as WHERE TO DO IT, not "which should I open?" — the old wording made a page name the
            // whole expected answer, so both the user and the classifier read the reply as a nav request.
            // An overflow is stated, never swallowed: three buttons that are secretly the top three of six
            // read as "these are your options", which is a lie the cap must not tell.
            message: `${spec.missing(here)} — I can do that on ${places}${
                overflow.length ? ", or another page if you name one" : ""
            }. Where would you like me to do that?`
        };
    }

    // Nowhere reachable. Still say where the action lives (from the full manifest) so the reply teaches
    // the platform instead of dead-ending.
    const homes = pagesMountingCapability(spec.capability).map(k => prose({ key: k, label: pageLabelOf(k) }));
    return {
        ...base,
        clarify: true,
        needsClarification: true,
        message: homes.length
            ? `${spec.missing(here)} — ${spec.home(joinProse(homes))}, which I can't open for you from here.`
            : `${spec.missing(here)}.`
    };
};

/**
 * The DATA-MISMATCH seam — the sibling of the capability redirect above. That one fires when the routed
 * capability is NOT mounted here; this one fires when it IS mounted but the utterance names ANOTHER page's
 * DATA: "export the recommendations of florida region" said on Buildings routes table_command (this page
 * has a table, tables export) and would happily export the BUILDINGS grid — records the user never asked
 * for. Buildings are not recommendations; the page manifest knows which records live where, and this is
 * where that knowledge gates execution.
 *
 * Returns a navigate+replay plan proposal when the utterance names exactly ONE other reachable page (and
 * not the current one), else null to proceed with the local table command. The replay shape follows the
 * destination's own capabilities — the same split the pageChoice residual-action guard uses:
 *   - destination mounts `filters` + the goal is an EXPORT with CRITERIA ("…of florida region"): navigate
 *     → filter there (the original utterance; Stage A extracts the criteria) → a deterministic export step
 *     (never the original text, which the table interpreter would re-read as a spurious row filter);
 *   - export with NO criteria ("export the recommendations"): navigate → the deterministic export step
 *     alone (an empty filter step would just stall the plan);
 *   - everything else (a pure table op on named data — "sort the recommendations by cost" — or a
 *     destination whose row filtering IS its table_view, e.g. buildings): navigate → one table_view replay.
 * Confirm-first like every plan (Run is the one confirmation); everything re-grounds on arrival.
 */

// Does the utterance carry data CRITERIA beyond the action words and the named page itself? Deterministic
// and deliberately rough: strip every way the target is named, the export/format vocabulary, and filler —
// any surviving token ("florida", "critical", "50k") is treated as criteria. A miss in either direction
// degrades softly (an extra clarifying filter card, or criteria grounding as a column filter on arrival) —
// and the classifier's fused-goal tell routes these to the planner anyway once deployed; this seam is the
// deterministic fallback.
const FORMAT_WORDS = ["word", "pdf", "doc", "docx"];
const CRITERIA_FILLER = new Set([
    "i", "want", "need", "would", "like", "to", "the", "a", "an", "of", "my", "our", "all", "get", "give",
    "me", "us", "please", "and", "for", "from", "that", "those", "these", "with", "as", "in", "on", "out",
    "file", "files", "list", "lists", "table", "tables", "data", "records", "record", "view", "page",
    "show", "see", "can", "you", "it", "them", "everything", "current"
]);
const hasCriteriaBeyond = (utterance, target) => {
    let norm = ` ${normalizeText(utterance)} `;
    // Longest names first so "recommendations list" goes before "recommendations".
    const names = [target.label, ...(target.synonyms || [])]
        .map(normalizeText)
        .filter(Boolean)
        .sort((a, b) => b.length - a.length);
    for (const n of names) norm = norm.split(` ${n} `).join(" ");
    return norm
        .split(" ")
        .filter(Boolean)
        .some(t => !CRITERIA_FILLER.has(t) && !EXPORT_WORDS.includes(t) && !FORMAT_WORDS.includes(t));
};

export const buildDataRedirectProposal = (routedKind, interpretResponse, ctx = {}) => {
    const { page, navTargets, utterance } = ctx;
    // Scope: table commands on pages that DO mount table_view (the unmounted case is the seam above).
    if (routedKind !== "table_command" || !isKnownPage(page)) return null;
    if (!pageMountsCapability(page, "table_view")) return null;

    // OPERABLE pages only: this seam must pick exactly ONE destination and bails when several are named, so
    // it may only consider pages Otto can actually act on. Without the scope, registering a tier-1/2 page
    // whose noun appears in ordinary filter criteria ("…of florida REGION") would make this seam ambiguous
    // and silently stop redirecting — a registry entry must never switch an existing guard off.
    const named = pagesNamedIn(utterance, navTargets, { minTier: TIERS.OPERABLE }).filter(t => isKnownPage(t.key));
    if (!named.length) return null; // no page's data named — the local table is what they meant
    if (named.some(t => t.key === page)) return null; // the CURRENT page was named — local is right
    if (named.length > 1) return null; // ambiguous across pages — don't guess; the classifier/planner owns it
    const target = { key: named[0].key, label: named[0].label };
    if (!pageMountsCapability(target.key, "table_view")) return null; // named data has no grid to act on

    const here = prose({ key: page, label: pageLabelOf(page) });
    const wantsExport = detectResidualAction(utterance) === "export";
    // Keep a named format ("as word") on the deterministic export step so it isn't asked about twice.
    const fmt = detectExportFormat(utterance);
    const exportStep = {
        capability: "table_view",
        sub_utterance: `export the table${fmt ? ` as ${fmt}` : ""}`,
        target_page: target.key,
        label: "Export the table"
    };
    const splitFilters = pageMountsCapability(target.key, "filters") && wantsExport;
    const navStep = {
        capability: "navigate",
        sub_utterance: `open the ${target.label} page`,
        target_page: target.key,
        label: pageActionLabel("Open", target)
    };
    const steps =
        splitFilters && hasCriteriaBeyond(utterance, named[0])
            ? [
                  navStep,
                  {
                      capability: "filters",
                      sub_utterance: utterance,
                      target_page: target.key,
                      label: describePlanStep({ capability: "filters", sub_utterance: utterance, target_page: target.key }, { [target.key]: target.label })
                  },
                  exportStep
              ]
            : splitFilters
              ? [navStep, exportStep]
              : [
                    navStep,
                    {
                        capability: "table_view",
                        sub_utterance: utterance,
                        target_page: target.key,
                        label: describePlanStep({ capability: "table_view", sub_utterance: utterance, target_page: target.key }, { [target.key]: target.label })
                    }
                ];
    return {
        capabilityRedirect: true,
        dataRedirect: true,
        requestId: interpretResponse && interpretResponse.request_id,
        sessionId: interpretResponse && interpretResponse.session_id,
        utterance,
        plan: true,
        needsClarification: false,
        planId: (interpretResponse && interpretResponse.request_id) || `data-redirect-${target.key}`,
        steps,
        options: [],
        message: `Those records live on ${pageReference(target)} — ${here} shows its own data. Want me to take you there and do that?`
    };
};

/**
 * Is this typed message an ANSWER to the redirect question Otto is holding open? Returns the matching
 * choice (whose `steps` are the same plan its button would run) or null to process the message normally.
 *
 * The same naming rule the question was built from, restricted to the pages actually offered — so the
 * answer is resolved against what Otto ASKED, not against the whole catalog. Deliberately strict: naming
 * none of the offered pages ("actually, filter by HVAC") or naming several is NOT an answer, and falls
 * through to the normal pipeline rather than guessing the user into a navigation.
 */
export const resolveRedirectAnswer = (text, pending, navTargets) => {
    // Accepts the proposal itself (`choices`, keyed by `key`) or a stored pending-question entry
    // (`options`, keyed by `id`) — the two shapes the typed-answer path sees.
    const choices = (pending && (pending.options || pending.choices)) || [];
    const keyOf = c => (c && (c.key != null ? c.key : c.id)) || null;
    if (!choices.length) return null;
    const offered = new Set(choices.map(keyOf).filter(Boolean));
    const named = pagesNamedIn(text, navTargets).filter(t => offered.has(t.key));
    if (named.length !== 1) return null;
    return choices.find(c => keyOf(c) === named[0].key) || null;
};
